import React, { useState, useEffect, useCallback } from 'react';
import {
  IconButton,
  Grid,
  Box,
  TextField,
  Autocomplete,
  styled,
  Tooltip,
  tooltipClasses,
} from '@mui/material';
import FilterIcon from '@mui/icons-material/FilterList';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Dialog } from '../../Components/MUIs';
import {
  isGridFilterTargetColumn,
  getGridFilterValue,
} from './gridFilterUtils';

const LargeTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#4CAF50',
    color: '#fff',
    fontSize: '0.9rem',
    padding: theme.spacing(1),
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#4CAF50',
  },
}));

function GridFilterButton({
  gridRef,
  gridApi: gridApiProp,
  gridColumnApi: gridColumnApiProp,
  rowData,
  selectedFiltersRef,
  selectedFilters = {},
  setSelectedFilters,
  setIsFiltered,
  setHeaderMap,
  headerMap = {}, // ✅ 부모에서 내려받아서 사용 (라벨 표시에 필요)
}) {
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [openFields, setOpenFields] = useState(new Set());

  const getGridApi = useCallback(
    () => gridApiProp || gridRef?.current?.api || null,
    [gridApiProp, gridRef]
  );
  const getGridColumnApi = useCallback(
    () => gridColumnApiProp || gridRef?.current?.columnApi || null,
    [gridColumnApiProp, gridRef]
  );

  const convertSelectedFiltersToModel = (selFilters) => {
    const model = {};
    if (!selFilters) return model;
    Object.keys(selFilters).forEach((field) => {
      if ((selFilters[field] || []).length > 0) {
        model[field] = { filterType: 'set', values: selFilters[field] };
      }
    });
    return model;
  };

  const recomputeFilterOptions = useCallback(
    (modelOverride = null) => {
      const gridApi = getGridApi();
      const gridColumnApi = getGridColumnApi();
      if (!gridApi || !gridColumnApi) return;

      const allCols = gridColumnApi.getAllGridColumns?.() || [];
      const colDefByField = new Map(
        allCols.map((col) => [col?.colDef?.field, col?.colDef]).filter(([f]) => f)
      );

      const visibleCols = allCols.filter((col) => {
        if (!col?.isVisible?.()) return false;
        return isGridFilterTargetColumn(col?.colDef);
      });

      const tmpOptions = {};
      const tmpHeaderMap = {};
      visibleCols.forEach((col) => {
        const field = col.colDef.field;
        tmpHeaderMap[field] = col.colDef.headerName || field;
        tmpOptions[field] = new Set();
        if (Array.isArray(col.colDef.filterOptions)) {
          col.colDef.filterOptions.forEach((opt) => tmpOptions[field].add(opt));
        }
      });

      const currentModel =
        modelOverride ||
        (selectedFiltersRef && selectedFiltersRef.current
          ? convertSelectedFiltersToModel(selectedFiltersRef.current)
          : gridApi.getFilterModel
            ? gridApi.getFilterModel()
            : {});

      visibleCols.forEach((col) => {
        const field = col.colDef.field;
        (rowData || []).forEach((row) => {
          let match = true;
          Object.keys(currentModel).forEach((k) => {
            if (k === field) return;
            const f = currentModel[k];
            if (f?.filterType === 'set') {
              const otherDef = colDefByField.get(k);
              const otherValue = getGridFilterValue(otherDef, row);
              if (!(f.values || []).includes(otherValue)) match = false;
            }
          });
          if (!match) return;

          const cellValue = getGridFilterValue(col.colDef, row);
          if (cellValue != null && cellValue !== '') {
            tmpOptions[field].add(cellValue);
          }
        });
      });

      Object.keys(tmpOptions).forEach((field) => {
        (selectedFilters[field] || []).forEach((v) => tmpOptions[field].add(v));
      });

      const finalOptions = {};
      Object.keys(tmpOptions).forEach(
        (k) => (finalOptions[k] = Array.from(tmpOptions[k]))
      );

      setFilterOptions((prev) => {
        const merged = { ...prev };
        Object.keys(finalOptions).forEach((k) => {
          if (!openFields.has(k)) merged[k] = finalOptions[k];
        });
        return merged;
      });

      setHeaderMap(tmpHeaderMap);

      if (Object.keys(selectedFilters).length === 0) {
        const init = {};
        Object.keys(finalOptions).forEach((k) => (init[k] = []));
        setSelectedFilters(init);
      }
    },
    [
      getGridApi,
      getGridColumnApi,
      rowData,
      selectedFilters,
      openFields,
      setHeaderMap,
      setSelectedFilters,
    ]
  );

  const applyFilterModelFromSelected = useCallback(
    (filtersOverride = null) => {
      const gridApi = gridRef.current?.api || getGridApi();
      if (!gridApi) return;

      const filters = filtersOverride || selectedFilters;
      if (selectedFiltersRef) selectedFiltersRef.current = filters;

      gridApi.onFilterChanged?.();

      setIsFiltered(Object.keys(filters).some((k) => filters[k]?.length > 0));

      setTimeout(() => {
        recomputeFilterOptions(convertSelectedFiltersToModel(filters));
      }, 0);
    },
    [
      selectedFilters,
      setIsFiltered,
      recomputeFilterOptions,
      gridRef,
      getGridApi,
      selectedFiltersRef,
    ]
  );

  const handleResetFilters = useCallback(() => {
    const gridApi = getGridApi();
    if (!gridApi) return;
    const init = {};
    Object.keys(filterOptions || {}).forEach((k) => (init[k] = []));
    setSelectedFilters(init);
    gridApi.setFilterModel(null);
    setIsFiltered(false);
    recomputeFilterOptions();
  }, [
    getGridApi,
    filterOptions,
    setSelectedFilters,
    setIsFiltered,
    recomputeFilterOptions,
  ]);

  useEffect(() => {
    const gridApi = getGridApi();
    if (!gridApi) return;
    applyFilterModelFromSelected(selectedFilters);
    recomputeFilterOptions();
  }, [
    selectedFilters,
    getGridApi,
    applyFilterModelFromSelected,
    recomputeFilterOptions,
  ]);

  return (
    <>
      <LargeTooltip title="필터" arrow placement="top">
        <IconButton
          sx={{
            ml: 2,
            border: 1,
            borderColor: '#9E9E9E',
            position: 'relative',
          }}
          onClick={() => setFilterDialogOpen(true)}
        >
          {!Object.values(selectedFilters).some((v) => v.length > 0) ? (
            <FilterIcon sx={{ color: '#9E9E9E' }} />
          ) : (
            <>
              <FilterIcon sx={{ color: '#1976d2' }} />
              <FiberManualRecordIcon
                sx={{
                  color: '#1976d2',
                  fontSize: 11,
                  position: 'absolute',
                  top: 4,
                  right: 4,
                }}
              />
            </>
          )}
        </IconButton>
      </LargeTooltip>

      <Dialog
        title="컬럼별 필터"
        fullWidth
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        content={
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {Object.keys(filterOptions).map((field) => (
                <Grid item xs={6} key={field}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={filterOptions[field] || []}
                    value={selectedFilters[field] || []}
                    onChange={(e, newValue) => {
                      setSelectedFilters((prev) => {
                        const updated = { ...prev, [field]: newValue };
                        applyFilterModelFromSelected(updated);
                        return updated;
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={headerMap[field] || field}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        }
        useSaveButton
        useCloseButton
        saveButtonName="초기화"
        closeButtonName="닫기"
        onSave={handleResetFilters}
      />
    </>
  );
}

export default GridFilterButton;
